import type {
  ColumnFiltersState,
  ColumnVisibilityState,
  SortingState,
} from "@tanstack/react-table";

import { useEffect, useMemo, useRef, useState } from "react";
import { useMatches, useSearchParams } from "react-router-dom";

import type { DataTableReactInstance } from "./table-features";

import { location } from "../../../../app/location";

const PAGE_KEY = "page";
const PAGE_SIZE_KEY = "pageSize";
const STORAGE_PREFIX = "DataTable.";

/**
 * The slices this hook restores. v9 dropped the `InitialTableState` type along
 * with the `initialState` option, and these four are all DataTable hydrates.
 */
interface RestoredState {
  pagination: { pageIndex: number; pageSize: number };
  sorting?: SortingState;
  columnFilters?: ColumnFiltersState;
  columnVisibility?: ColumnVisibilityState;
}

interface SessionState {
  sorting?: SortingState;
  columnFilters?: ColumnFiltersState;
  columnVisibility?: ColumnVisibilityState;
}

/**
 * Stable, route-scoped key for persisted table state. Uses the leaf match's
 * `id` — for data routers this is unique per route definition and inherently
 * parameter-free, so no pattern reconstruction is needed. An optional
 * `extraKey` (e.g. an active tab id) scopes persistence to a sub-context of
 * the same route. When `extraKey` changes, callers must remount DataTable
 * via React `key={extraKey}` so the initial-state hydration aligns with the
 * write-back key.
 */
export function useTablePersistenceRouteName(
  active: boolean,
  extraKey?: string,
): string | undefined {
  const matches = useMatches();
  return useMemo(() => {
    if (!active) return undefined;
    const id = matches[matches.length - 1]?.id;
    if (!id) return undefined;
    return extraKey != null ? `${id}|${extraKey}` : id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, extraKey]);
}

export function useTablePersistenceInitial({
  active,
  defaultPageSize,
  routeName,
}: {
  active: boolean;
  defaultPageSize: number;
  routeName: string | undefined;
}): RestoredState {
  const [initialState] = useState((): RestoredState => {
    if (!active) {
      return { pagination: { pageIndex: 0, pageSize: defaultPageSize } };
    }
    // Read the live URL, not `useSearchParams()`: when a tab switch remounts
    // this table via `key=`, the MobX-driven remount runs before React
    // Router's `setSearchParams` (which cleared `page`) has propagated to the
    // hook snapshot — so the snapshot still holds the previous tab's `page`.
    // `location` already has the new URL. (The write-back effect reads the
    // live URL for the same reason.)
    const params = new URL(location.href).searchParams;
    const pageRaw = parseInt(params.get(PAGE_KEY) ?? "", 10);
    const sizeRaw = parseInt(params.get(PAGE_SIZE_KEY) ?? "", 10);
    const pageIndex = isNaN(pageRaw) ? 0 : Math.max(pageRaw - 1, 0);
    const pageSize = isNaN(sizeRaw) ? defaultPageSize : sizeRaw;

    const session = routeName ? loadSession(routeName) : undefined;

    return {
      pagination: { pageIndex, pageSize },
      // Left undefined when there's no session so the caller can fall back to
      // column defaults (initSort / initialColumnFilters) — a saved empty array
      // means the user explicitly cleared sorting/filters and must stay empty.
      sorting: session?.sorting,
      columnFilters: session?.columnFilters,
      // Sanitized against the current columns by `initialColumnVisibility`.
      columnVisibility: session?.columnVisibility,
    };
  });

  return initialState;
}

export function useTablePersistenceWriteBack<T>({
  table,
  active,
  defaultPageSize,
  routeName,
}: {
  table: DataTableReactInstance<T>;
  active: boolean;
  defaultPageSize: number;
  routeName: string | undefined;
}) {
  const [, setSearchParams] = useSearchParams();
  const state = table.state;
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (!active) return;
    const currentPage = state.pagination.pageIndex + 1;
    const currentPageSize = state.pagination.pageSize;
    // Read live URL instead of using the functional updater's `prev`: the
    // updater receives the searchParams captured in the closure (e.g. when
    // DataTable first rendered), which is stale if another effect already
    // navigated.
    const next = new URL(location.href).searchParams;
    next.delete(PAGE_KEY);
    next.delete(PAGE_SIZE_KEY);
    if (currentPage > 1) next.set(PAGE_KEY, String(currentPage));
    if (currentPageSize !== defaultPageSize)
      next.set(PAGE_SIZE_KEY, String(currentPageSize));
    setSearchParams(next, { replace: true });
  }, [
    active,
    state.pagination.pageIndex,
    state.pagination.pageSize,
    defaultPageSize,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!active || !routeName) return;
    // Skip the first run — that's the hydrated initial state, no need to rewrite.
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    saveSession(routeName, {
      sorting: state.sorting,
      columnFilters: state.columnFilters,
      columnVisibility: state.columnVisibility,
    });
  }, [
    active,
    routeName,
    state.sorting,
    state.columnFilters,
    state.columnVisibility,
  ]);
}

function loadSession(routeName: string): SessionState | undefined {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + routeName);
    if (!raw) return undefined;
    return JSON.parse(raw) as SessionState;
  } catch (error) {
    console.warn("Cannot read DataTable session state", error);
    return undefined;
  }
}

function saveSession(routeName: string, state: SessionState) {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + routeName, JSON.stringify(state));
  } catch (error) {
    console.warn("Cannot persist DataTable session state", error);
  }
}
